import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useAuth, useGet } from "@/context/AuthContext";
import { api, errMsg, saveRecord, localDate } from "@/lib/api";
import { timeAgo } from "@/lib/fmt";
import { PageHeader, Loading, Section, EmptyState } from "@/components/app/Bits";
import { Chips } from "@/components/app/RecordForm";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  ["business", "Business", "Sales pacing, guest volume, important reservations"],
  ["staffing", "Staffing", "Call-offs, late employees, coverage changes"],
  ["kitchen", "Kitchen", "86'd products, prep concerns, equipment"],
  ["bar", "Bar", "Inventory concerns, 86'd beverages, shortages"],
  ["guests", "Guests", "VIPs, complaints, follow-ups"],
  ["facilities", "Facilities", "Maintenance issues"],
  ["next_shift", "Next shift needs to know", "Anything else"],
];

export default function Handoff() {
  const { user } = useAuth();
  const { data, isLoading, refetch } = useGet("/handoffs?limit=10");
  const [v, setV] = useState({ shift: "Lunch → Dinner" });
  const [busy, setBusy] = useState(false);
  const items = data?.items || [];
  const pending = items.find((h) => !h.reviewed && h.created_by !== user?.id);
  const submit = async (e) => {
    e.preventDefault();
    if (!SECTIONS.some(([k]) => v[k])) return toast.error("Add at least one note for the next manager");
    setBusy(true);
    try {
      const r = await saveRecord("/handoffs", { ...v, date: localDate() });
      toast.success(r.queued ? "Saved offline — will sync" : "Handoff sent to the next manager");
      setV({ shift: v.shift });
      refetch();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const review = async (h) => {
    try { await api.patch(`/handoffs/${h.id}`, { reviewed: true }); toast.success("Handoff reviewed"); refetch(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="handoff-page">
      <PageHeader eyebrow="Operate" title="Shift Handoff" subtitle="The outgoing manager fills this in; the incoming manager reviews it. Nothing falls through the cracks." />
      {isLoading ? <Loading /> : pending && (
        <div className="card-surface border-2 border-[#9E2A2B] p-5 md:p-6 mb-8" data-testid="incoming-handoff">
          <div className="font-display text-2xl">Shift Handoff — {SECTIONS.filter(([k]) => pending[k]).length} items need your attention</div>
          <div className="text-sm text-stone-500 mt-1">From {pending.created_by_name} · {pending.shift} · {timeAgo(pending.created_at)}</div>
          <div className="grid md:grid-cols-2 gap-4 mt-5">
            {SECTIONS.filter(([k]) => pending[k]).map(([k, t]) => <div key={k} className="rounded-lg bg-[#FBF9F5] p-4"><div className="label-eyebrow mb-1">{t}</div><div className="text-sm">{pending[k]}</div></div>)}
          </div>
          <Button className="mt-5 h-12 w-full md:w-auto px-8" onClick={() => review(pending)} data-testid="handoff-reviewed-btn"><CheckCircle2 className="h-4 w-4 mr-2" />Handoff Reviewed</Button>
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="lg:col-span-2 space-y-4" data-testid="handoff-form">
          <Chips options={["Open → Lunch", "Lunch → Dinner", "Dinner → Close"]} value={v.shift} onChange={(s) => setV({ ...v, shift: s })} testid="handoff-shift" />
          {SECTIONS.map(([k, t, hint]) => (
            <label key={k} className="block card-surface p-4">
              <div className="font-medium">{t}</div><div className="text-xs text-stone-500 mb-2">{hint}</div>
              <textarea rows={2} value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`handoff-${k}`} className="w-full rounded-lg border border-input p-3 text-base" />
            </label>
          ))}
          <Button disabled={busy} className="w-full h-12 text-base" data-testid="handoff-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Send handoff</Button>
        </form>
        <Section title="Recent handoffs">
          {!items.length ? <EmptyState title="No handoffs yet" text="Your first handoff shows up here for the next manager." /> : (
            <div className="space-y-2">{items.map((h) => (
              <div key={h.id} className="card-surface p-4 text-sm" data-testid={`handoff-${h.id}`}>
                <div className="font-medium">{h.shift}</div>
                <div className="text-xs text-stone-500">{h.created_by_name} · {timeAgo(h.created_at)}</div>
                <div className="text-xs mt-2">{h.reviewed ? <span className="text-emerald-700">✓ Reviewed by {h.reviewed_by}</span> : <span className="text-amber-700">Awaiting review</span>}</div>
              </div>))}</div>
          )}
        </Section>
      </div>
    </div>
  );
}
